import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowRight, 
  Users, 
  Building2, 
  Monitor,
  Heart,
  Sparkles,
  Radio,
  Globe,
  Mail,
  ChevronDown,
  Search,
  MapPin
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ColaboraPage = () => {
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [bravoCinemas, setBravoCinemas] = useState([]);
  const [cinemaSearch, setCinemaSearch] = useState('');
  const [cinemaSort, setCinemaSort] = useState({ key: 'nombre', dir: 'asc' });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/bravo-cinemas/active`)
      .then(res => res.json())
      .then(data => {
        setBravoCinemas(data);
        // Scroll to #red-bravo after data loads
        if (location.hash === '#red-bravo') {
          setTimeout(() => {
            const el = document.getElementById('red-bravo');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
        }
      })
      .catch(() => {});
  }, []);

  const filteredCinemas = bravoCinemas.filter(c => {
    if (!cinemaSearch) return true;
    const q = cinemaSearch.toLowerCase();
    return (c.nombre || '').toLowerCase().includes(q) ||
           (c.circuito || '').toLowerCase().includes(q) ||
           (c.poblacion || '').toLowerCase().includes(q) ||
           (c.provincia || '').toLowerCase().includes(q) ||
           (c.comunidad_autonoma || '').toLowerCase().includes(q);
  }).sort((a, b) => {
    const valA = (a[cinemaSort.key] || '').toLowerCase();
    const valB = (b[cinemaSort.key] || '').toLowerCase();
    return cinemaSort.dir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  const toggleSort = (key) => {
    setCinemaSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const SortIcon = ({ col }) => (
    <span className="inline-block ml-1 opacity-50">
      {cinemaSort.key === col ? (cinemaSort.dir === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  // Highlight component for key phrases
  const Highlight = ({ children }) => (
    <span className="text-[#e4002b] font-semibold">{children}</span>
  );

  const collaborationTypes = [
    {
      id: 'bravo',
      icon: Radio,
      title: 'Únete a la Red Bravo',
      subtitle: 'Tu sala puede ser el próximo gran escenario mundial',
      description: 'Cuando decides instalar la tecnología Bravo en tu centro, decides apoyar, difundir y sumar valor a tu oferta cultural. Y esto tiene un valor incalculable para tus ciudadanos.',
      features: [
        'Directos de la Royal Opera House y la Filarmónica de Berlín',
        'Sin satélite ni servidores de cine complejos',
        'Dispositivo y soporte técnico incluido',
        'Revitaliza tu programación de forma activa'
      ],
      cta: 'QUIERO INSTALAR BRAVO',
      ctaLink: '/cinema/register',
      image: 'https://images.unsplash.com/photo-1761173084851-1e5302e931fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      id: 'programador',
      icon: Monitor,
      title: 'Hazte Programador de Contenidos',
      subtitle: 'Una temporada de éxito puede marcar la diferencia en la vida cultural de una ciudad',
      description: 'Ya sea mediante una programación puntual o una temporada completa de ópera y ballet, tu apuesta por los contenidos de Versión Digital nos permite continuar produciendo y distribuyendo cultura de calidad.',
      features: [
        'Diversifica la taquilla de tu cine comercial',
        'Excelencia sin riesgos logísticos',
        'Sin envíos de DCP ni claves KDM',
        'Cada pase es una oportunidad de dignidad cultural'
      ],
      cta: 'VER CATÁLOGO',
      ctaLink: '/catalogo',
      image: 'https://images.unsplash.com/photo-1767725832189-d9f03440ce0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    },
    {
      id: 'instituciones',
      icon: Building2,
      title: 'Colabora como Institución o Empresa',
      subtitle: 'Grandes entidades y ayuntamientos ya confían en nosotros',
      description: 'Detrás de cada proyección hay muchas manos que empujan. Algunas de ellas pertenecen a instituciones valientes, comprometidas y humanas que creen en la responsabilidad social a través del arte.',
      features: [
        'Refuerza tu compromiso con la inclusión cultural',
        'Desarrollo local a través de las artes',
        'Lleva cultura a auditorios, colegios y centros culturales',
        'Únete a nuestros partners actuales'
      ],
      cta: 'SOLICITAR INFORMACIÓN',
      ctaLink: 'mailto:marketing@versiondigital.es?subject=Colaboración%20institucional%20-%20CinemaStreaming',
      isEmail: true,
      image: 'https://images.unsplash.com/photo-1770101516572-d8d85d392d75?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
    }
  ];

  const faqs = [
    {
      question: '¿Qué es la Red Bravo?',
      answer: 'La Red Bravo es una comunidad de cines y auditorios que utilizan nuestra tecnología para proyectar contenidos culturales de alta calidad. Al unirte, tu sala se convierte en un punto de acceso a los mejores escenarios del mundo.'
    },
    {
      question: '¿Necesito equipamiento especial?',
      answer: 'No. Con Bravo no necesitas satélite ni servidores de cine complejos. Te facilitamos el dispositivo y todo el soporte técnico necesario para empezar a proyectar.'
    },
    {
      question: '¿Puedo programar eventos puntuales o necesito comprometerme a una temporada completa?',
      answer: 'Ambas opciones son válidas. Puedes optar por eventos puntuales o programar una temporada completa según las necesidades de tu sala y tu audiencia.'
    },
    {
      question: '¿Cómo pueden colaborar las instituciones públicas?',
      answer: 'Las instituciones pueden colaborar patrocinando proyecciones, facilitando espacios para eventos culturales, o apoyando económicamente la difusión de contenidos en zonas con menor acceso a la cultura.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="colabora-page">
      <Header onOpenAuth={() => setShowAuth(true)} onOpenSearch={() => setShowSearch(true)} />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1766844649143-af98d71e346b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" 
            alt="Sala de cine con pantalla iluminada"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-1 bg-[#e4002b]" />
              <span className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase">Colabora con nosotros</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Tu pantalla transforma
              <span className="text-[#e4002b]"> tu comunidad</span>
            </h1>
            
            <p className="text-2xl md:text-3xl text-white/90 mb-4 font-light">
              <Highlight>Cada butaca cuenta</Highlight>
            </p>
            
            <p className="text-lg md:text-xl text-white/70 mb-8 leading-relaxed max-w-2xl">
              En CinemaStreaming, de la mano de <span className="text-white font-semibold">Versión Digital</span>, 
              trabajamos cada día para <Highlight>democratizar el acceso a la alta cultura</Highlight> y 
              llevar los grandes escenarios del mundo a cada rincón. Pero no podemos hacerlo solos: 
              el cine es una experiencia compartida y <span className="text-white font-semibold">te necesitamos para abrir el telón</span>.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="#colaborar" 
                className="px-8 py-4 bg-[#e4002b] text-white font-semibold rounded-lg hover:bg-[#c70024] transition-all flex items-center gap-2"
                data-testid="hero-colaborar-btn"
              >
                Descubre cómo colaborar <ArrowRight className="w-5 h-5" />
              </a>
              <Link 
                to="/cinema/register" 
                className="px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
                data-testid="hero-register-btn"
              >
                Registra tu sala
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Intro Text Section */}
      <section className="py-16 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
            Existen muchas formas de colaborar para que <Highlight>la ópera, el ballet y los grandes conciertos</Highlight> lleguen 
            a tu localidad. Ya seas un cine comercial, un auditorio municipal o una institución comprometida, 
            tu apuesta por la cultura genera un <Highlight>impacto real en tu audiencia</Highlight>.
          </p>
        </div>
      </section>

      {/* Collaboration Types */}
      <section id="colaborar" className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Descubre cómo puedes <span className="text-[#e4002b]">formar parte</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Elige la forma de colaboración que mejor se adapte a tu sala o institución
            </p>
          </div>

          <div className="space-y-24">
            {collaborationTypes.map((collab, index) => (
              <div 
                key={collab.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#e4002b]/20 flex items-center justify-center">
                      <collab.icon className="w-6 h-6 text-[#e4002b]" />
                    </div>
                    <div className="w-10 h-1 bg-[#e4002b]" />
                  </div>
                  
                  <h3 className="text-2xl md:text-4xl font-bold text-white mb-3">
                    {collab.title}
                  </h3>
                  
                  <p className="text-[#e4002b] text-lg font-medium mb-4">
                    {collab.subtitle}
                  </p>
                  
                  <p className="text-white/70 text-lg leading-relaxed mb-6">
                    {collab.description}
                  </p>

                  <ul className="space-y-3 mb-8">
                    {collab.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#e4002b]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 rounded-full bg-[#e4002b]" />
                        </div>
                        <span className="text-white/80">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {collab.isEmail ? (
                    <a 
                      href={collab.ctaLink}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-[#e4002b] text-white font-semibold rounded-lg hover:bg-[#c70024] transition-all"
                      data-testid={`cta-${collab.id}`}
                    >
                      <Mail className="w-5 h-5" />
                      {collab.cta}
                    </a>
                  ) : (
                    <Link 
                      to={collab.ctaLink}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-[#e4002b] text-white font-semibold rounded-lg hover:bg-[#c70024] transition-all"
                      data-testid={`cta-${collab.id}`}
                    >
                      {collab.cta}
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  )}
                </div>

                {/* Image */}
                <div className={`relative rounded-2xl overflow-hidden aspect-[4/3] ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img 
                    src={collab.image}
                    alt={collab.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className={`absolute inset-0 bg-gradient-to-${index % 2 === 0 ? 'l' : 'r'} from-[#0a0a0a]/30 via-transparent to-transparent`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img 
                  src="https://images.unsplash.com/photo-1764936394584-c4a66ac31e00?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                  alt="Cortinas de teatro rojas"
                  className="w-full aspect-[3/4] object-cover rounded-xl"
                />
                <img 
                  src="https://images.unsplash.com/photo-1647187835067-a58451e3edf6?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                  alt="Bailarina en escenario"
                  className="w-full aspect-square object-cover rounded-xl"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img 
                  src="https://images.unsplash.com/photo-1770101516721-bc6b96af7db3?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                  alt="Orquesta sinfónica"
                  className="w-full aspect-square object-cover rounded-xl"
                />
                <img 
                  src="https://images.unsplash.com/photo-1760897008023-db0414e0c6f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                  alt="Audiencia en teatro"
                  className="w-full aspect-[3/4] object-cover rounded-xl"
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-1 bg-[#e4002b]" />
                <span className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase">Tu impacto</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                ¿Quieres apoyar nuestra labor y <span className="text-[#e4002b]">formar parte del cambio</span>?
              </h2>
              
              <p className="text-white/70 text-lg leading-relaxed mb-6">
                Con tu apoyo, podremos seguir ofreciendo recursos, <Highlight>tecnología Bravo</Highlight> y 
                acompañamiento para llevar la belleza de las artes escénicas a quienes más lo necesitan.
              </p>
              
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Porque hay eventos que no se olvidan y apuestas que definen la identidad de una sala. 
                Tu compromiso y tu pantalla pueden marcar la diferencia. Únete como sala colaboradora 
                y participa en la mayor red de difusión de contenidos alternativos.
              </p>

              <div className="bg-[#0a0a0a] rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#e4002b]/20 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-6 h-6 text-[#e4002b]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Comunidades más cultas</h4>
                    <p className="text-white/60 text-sm">
                      Tu apuesta genera dignidad cultural y futuro para los espectadores de tu localidad
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bravo Cinema Network Section */}
      {bravoCinemas.length > 0 && (
        <section className="py-24 md:py-32 bg-[#0a0a0a]" id="red-bravo" data-testid="cinema-network-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-1 bg-[#e4002b]" />
                <span className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase">Red Bravo</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                El nuevo estándar de la exhibición cinematográfica <span className="text-[#e4002b]">ya está aquí</span>
              </h2>
              <p className="text-lg text-white/60 leading-relaxed max-w-3xl mb-4">
                La comunidad Bravo no para de crecer. Lo que empezó como una solución para acceder a contenidos exclusivos
                con total comodidad técnica, se ha convertido en una herramienta indispensable gracias a su fiabilidad y servicio diario.
              </p>
              <p className="text-lg text-white/80 font-medium">
                Únete a las salas que ya han automatizado su cabina y transformado su cartelera:
              </p>
            </div>

            {/* Search */}
            <div className="relative max-w-md mb-8">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 w-5 h-5" />
              <input
                type="text"
                value={cinemaSearch}
                onChange={(e) => setCinemaSearch(e.target.value)}
                placeholder="Buscar por nombre, ciudad, provincia..."
                className="w-full bg-white/[0.02] border border-white/10 text-white placeholder:text-white/30 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#e4002b] focus:border-[#e4002b] transition-all"
                data-testid="cinema-search-input"
              />
            </div>

            {/* Table */}
            <div className="rounded-xl border border-white/10 bg-white/[0.01] overflow-hidden" data-testid="cinema-table-container">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-white/[0.03] border-b border-white/10">
                      <th onClick={() => toggleSort('nombre')} className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white/40 cursor-pointer hover:text-white/70 select-none">Cine<SortIcon col="nombre" /></th>
                      <th onClick={() => toggleSort('circuito')} className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white/40 cursor-pointer hover:text-white/70 select-none">Circuito<SortIcon col="circuito" /></th>
                      <th onClick={() => toggleSort('poblacion')} className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white/40 cursor-pointer hover:text-white/70 select-none">Población<SortIcon col="poblacion" /></th>
                      <th onClick={() => toggleSort('provincia')} className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white/40 cursor-pointer hover:text-white/70 select-none">Provincia<SortIcon col="provincia" /></th>
                      <th onClick={() => toggleSort('comunidad_autonoma')} className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white/40 cursor-pointer hover:text-white/70 select-none">Comunidad Autónoma<SortIcon col="comunidad_autonoma" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCinemas.map((cinema, idx) => (
                      <tr key={cinema.id} className="border-b border-white/5 hover:bg-white/[0.04] transition-colors" data-testid={`cinema-row-${idx}`}>
                        <td className="px-6 py-4 text-sm font-medium text-white">{cinema.nombre}</td>
                        <td className="px-6 py-4 text-sm text-white/60">{cinema.circuito}</td>
                        <td className="px-6 py-4 text-sm text-white/60">{cinema.poblacion}</td>
                        <td className="px-6 py-4 text-sm text-white/60">{cinema.provincia}</td>
                        <td className="px-6 py-4 text-sm text-white/60">{cinema.comunidad_autonoma}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredCinemas.length === 0 && cinemaSearch && (
                <div className="text-center py-12 text-white/40">
                  <MapPin className="w-8 h-8 mx-auto mb-3 opacity-50" />
                  <p>No se encontraron resultados para "{cinemaSearch}"</p>
                </div>
              )}
              <div className="px-6 py-3 bg-white/[0.02] border-t border-white/10 text-right">
                <span className="text-white/30 text-sm">{filteredCinemas.length} {filteredCinemas.length === 1 ? 'sala' : 'salas'}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Preguntas <span className="text-[#e4002b]">frecuentes</span>
            </h2>
            <p className="text-white/60">
              Resolvemos tus dudas sobre cómo colaborar
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#141414] rounded-xl border border-white/10 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                  data-testid={`faq-${index}`}
                >
                  <span className="text-white font-medium pr-4">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-[#e4002b] flex-shrink-0 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-white/70 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-[#e4002b]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Globe className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            ¿Te gustaría colaborar y ayudarnos a crear comunidades más cultas?
          </h2>
          <p className="text-white/90 text-xl mb-10 max-w-2xl mx-auto">
            Tu compromiso y tu pantalla pueden marcar la diferencia. Únete a la mayor red de 
            difusión de contenidos culturales alternativos.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/cinema/register" 
              className="px-10 py-4 bg-white text-[#e4002b] font-bold rounded-lg hover:bg-white/90 transition-all text-lg"
              data-testid="final-cta-register"
            >
              Quiero instalar Bravo
            </Link>
            <a 
              href="mailto:marketing@versiondigital.es?subject=Colaboración%20-%20CinemaStreaming"
              className="px-10 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-all text-lg flex items-center gap-2"
              data-testid="final-cta-contact"
            >
              <Mail className="w-5 h-5" />
              Contactar
            </a>
          </div>
          <p className="text-white/70 text-sm mt-8">
            marketing@versiondigital.es
          </p>
        </div>
      </section>

      <Footer />

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}
    </div>
  );
};

export default ColaboraPage;
